import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { getAttribution } from '@/hooks/useAttribution';

const API = '';
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const preselectedProduct = searchParams.get('product') || '';

  const [form, setForm] = useState({
    name: '', company: '', country: '', email: '', phone: '',
    product_interest: preselectedProduct, quantity: '', message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (preselectedProduct) {
      setForm(f => ({ ...f, product_interest: preselectedProduct }));
    }
  }, [preselectedProduct]);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const attribution = getAttribution();
      const payload = { ...form, attribution };
      const { data } = await axios.post(`${API}/api/quote`, payload);
      setSuccess(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || '').join('. '));
      else setError('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-saffron-50/30" />
        <div className="relative max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">Get In Touch</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-earth-dark mt-3 tracking-tight">
              Request a Quote
            </h1>
            <p className="mt-4 text-lg text-earth/60 font-sans max-w-xl leading-relaxed">
              Fill out the form below and our export team will get back to you within 24 hours with pricing and availability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form + Contact Info */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Form */}
          <div className="lg:col-span-2">
            {success ? (
              <motion.div initial="hidden" animate="visible" variants={fadeUp} className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift p-12 text-center" data-testid="quote-success">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h2 className="font-display text-2xl font-bold text-earth-dark">Inquiry Submitted!</h2>
                <p className="mt-3 text-earth/60 font-sans">Reference: <strong className="text-saffron">#{success.id}</strong></p>
                <p className="mt-2 text-earth/60 font-sans">{success.message}</p>
                <button onClick={() => { setSuccess(null); setForm({ name: '', company: '', country: '', email: '', phone: '', product_interest: '', quantity: '', message: '' }); }} className="mt-6 text-saffron font-sans font-semibold text-sm hover:text-saffron-dark transition-colors" data-testid="submit-another">
                  Submit Another Inquiry
                </button>
              </motion.div>
            ) : (
              <motion.form
                initial="hidden" animate="visible" variants={fadeUp}
                onSubmit={handleSubmit}
                className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift p-6 sm:p-8 space-y-5"
                data-testid="quote-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1.5 block">Full Name *</label>
                    <input name="name" value={form.name} onChange={handleChange} required className="w-full border border-earth/15 rounded-md px-4 py-3 font-sans text-sm text-earth-dark placeholder:text-earth/30 focus:outline-none focus:ring-2 focus:ring-saffron/30 focus:border-saffron bg-cream/30" placeholder="Your full name" data-testid="form-name" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1.5 block">Company *</label>
                    <input name="company" value={form.company} onChange={handleChange} required className="w-full border border-earth/15 rounded-md px-4 py-3 font-sans text-sm text-earth-dark placeholder:text-earth/30 focus:outline-none focus:ring-2 focus:ring-saffron/30 focus:border-saffron bg-cream/30" placeholder="Company name" data-testid="form-company" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1.5 block">Country *</label>
                    <input name="country" value={form.country} onChange={handleChange} required className="w-full border border-earth/15 rounded-md px-4 py-3 font-sans text-sm text-earth-dark placeholder:text-earth/30 focus:outline-none focus:ring-2 focus:ring-saffron/30 focus:border-saffron bg-cream/30" placeholder="Country" data-testid="form-country" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1.5 block">Email *</label>
                    <input name="email" type="email" value={form.email} onChange={handleChange} required className="w-full border border-earth/15 rounded-md px-4 py-3 font-sans text-sm text-earth-dark placeholder:text-earth/30 focus:outline-none focus:ring-2 focus:ring-saffron/30 focus:border-saffron bg-cream/30" placeholder="email@company.com" data-testid="form-email" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1.5 block">Phone</label>
                    <input name="phone" value={form.phone} onChange={handleChange} className="w-full border border-earth/15 rounded-md px-4 py-3 font-sans text-sm text-earth-dark placeholder:text-earth/30 focus:outline-none focus:ring-2 focus:ring-saffron/30 focus:border-saffron bg-cream/30" placeholder="+91 92845 48218" data-testid="form-phone" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1.5 block">Product Interest *</label>
                    <select name="product_interest" value={form.product_interest} onChange={handleChange} required className="w-full border border-earth/15 rounded-md px-4 py-3 font-sans text-sm text-earth-dark focus:outline-none focus:ring-2 focus:ring-saffron/30 focus:border-saffron bg-cream/30" data-testid="form-product">
                      <option value="">Select a product</option>
                      <option value="Turmeric Powder">Turmeric Powder</option>
                      <option value="Red Chilli Powder">Red Chilli Powder</option>
                      <option value="Coriander Powder">Coriander Powder</option>
                      <option value="Garlic Powder">Garlic Powder</option>
                      <option value="Cumin Powder">Cumin Powder</option>
                      <option value="Black Pepper Powder">Black Pepper Powder</option>
                      <option value="Multiple">Multiple Products</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1.5 block">Quantity Required</label>
                  <input name="quantity" value={form.quantity} onChange={handleChange} className="w-full border border-earth/15 rounded-md px-4 py-3 font-sans text-sm text-earth-dark placeholder:text-earth/30 focus:outline-none focus:ring-2 focus:ring-saffron/30 focus:border-saffron bg-cream/30" placeholder="e.g., 5 MT, 20 FCL, etc." data-testid="form-quantity" />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-earth/50 font-sans font-semibold mb-1.5 block">Message</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={4} className="w-full border border-earth/15 rounded-md px-4 py-3 font-sans text-sm text-earth-dark placeholder:text-earth/30 focus:outline-none focus:ring-2 focus:ring-saffron/30 focus:border-saffron bg-cream/30 resize-none" placeholder="Any specific requirements, grades, or packaging preferences..." data-testid="form-message" />
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm font-sans rounded" data-testid="form-error">{error}</div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-saffron text-white font-sans font-semibold text-sm py-4 rounded-md hover:bg-saffron-dark transition-colors shadow-saffron-glow disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="form-submit"
                >
                  <Send size={16} />
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </motion.form>
            )}
          </div>

          {/* Contact Info Sidebar */}
          <div className="space-y-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift p-6" data-testid="contact-info">
              <h3 className="font-display text-lg font-bold text-earth-dark mb-5">Contact Information</h3>
              <div className="space-y-4">
                <a href="mailto:asjklaxmiglobal@gmail.com" className="flex items-start gap-3 text-sm text-earth/70 hover:text-saffron transition-colors font-sans group" data-testid="contact-email">
                  <div className="w-10 h-10 rounded-lg bg-saffron/10 flex items-center justify-center flex-shrink-0 group-hover:bg-saffron/20 transition-colors">
                    <Mail size={16} className="text-saffron" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-earth/40 font-semibold mb-0.5">Email</p>
                    <p>asjklaxmiglobal@gmail.com</p>
                  </div>
                </a>
                <a href="tel:+919284548218" className="flex items-start gap-3 text-sm text-earth/70 hover:text-saffron transition-colors font-sans group" data-testid="contact-phone">
                  <div className="w-10 h-10 rounded-lg bg-saffron/10 flex items-center justify-center flex-shrink-0 group-hover:bg-saffron/20 transition-colors">
                    <Phone size={16} className="text-saffron" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-earth/40 font-semibold mb-0.5">Phone</p>
                    <p>+91 92845 48218</p>
                  </div>
                </a>
                <div className="flex items-start gap-3 text-sm text-earth/70 font-sans">
                  <div className="w-10 h-10 rounded-lg bg-saffron/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={16} className="text-saffron" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-earth/40 font-semibold mb-0.5">Address</p>
                    <p>Nashik T Point, Chhatrapati Sambhajinagar, Maharashtra 431115, India</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* WhatsApp Card */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.1 }}>
              <a
                href="https://wa.me/919284548218?text=Hello%2C%20I%27m%20interested%20in%20your%20spice%20exports.%20Please%20share%20details."
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-[#25D366] text-white rounded-lg p-6 shadow-card-lift hover:shadow-card-hover transition-all group"
                data-testid="contact-whatsapp"
              >
                <div className="flex items-center gap-3">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  <div>
                    <p className="font-sans font-bold text-lg">Chat on WhatsApp</p>
                    <p className="text-white/80 text-sm font-sans">Instant response for bulk inquiries</p>
                  </div>
                </div>
              </a>
            </motion.div>

            {/* Map */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.2 }}>
              <div className="bg-white rounded-lg border border-saffron-100/50 shadow-card-lift overflow-hidden" data-testid="map-embed">
                <iframe
                  title="ASJK Laxmi Global Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d60190.39428413553!2d75.28979!3d19.8762!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bdb9815a369bc63%3A0x712d538b29a2cedb!2sChhatrapati%20Sambhajinagar%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                  width="100%"
                  height="250"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </motion.div>

            {/* Export Manager */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.3 }} className="bg-earth-dark rounded-lg p-6 text-cream-warm" data-testid="export-manager">
              <p className="text-[10px] uppercase tracking-wider text-saffron font-sans font-semibold mb-3">Export Manager</p>
              <h3 className="font-display text-lg font-bold text-white">Anurag Kamble</h3>
              <p className="text-sm text-cream-muted/60 font-sans mt-1">ASJK Laxmi Global</p>
              <a href="https://asjklaxmiglobal.com" target="_blank" rel="noopener noreferrer" className="text-xs text-saffron/70 font-sans mt-2 block hover:text-saffron transition-colors">
                asjklaxmiglobal.com
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
